import { type StatusIntent } from '@apps/web/components/ui/status_tokens';

// Light colors follow the agent template catalog. Dark variants use the
// shared app palette so result cards remain readable inside dark chat.

// .tcard / .approval-card: 14px radius, Grey 100 hairline, soft card shadow.
export const TEMPLATE_CARD_CLASS =
    'max-w-full min-w-0 overflow-hidden rounded-[14px] border ' +
    'border-[#DBE3E3] dark:border-border bg-white dark:bg-card text-[#002429] dark:text-foreground ' +
    'shadow-[0_1px_2px_rgba(0,36,41,0.04),0_4px_14px_rgba(0,36,41,0.05)]';

// Shared T-card anatomy. Keeping these pieces together prevents individual
// templates from drifting back to the app's legacy tool-card chrome.
export const TEMPLATE_CARD_HEADER_CLASS =
    'flex items-center gap-2 border-b border-[#E9F0F0] dark:border-border px-3.5 py-3';

export const TEMPLATE_CARD_HEADER_LARGE_CLASS = `${TEMPLATE_CARD_HEADER_CLASS} items-start`;

export const TEMPLATE_CARD_TITLE_CLASS =
    'text-[13px] font-semibold text-[#002429] dark:text-foreground';

export const TEMPLATE_CARD_TITLE_LARGE_CLASS =
    'text-sm font-semibold text-[#002429] dark:text-foreground';

export const TEMPLATE_CARD_SUBTITLE_CLASS =
    'text-xs text-[#617578] dark:text-muted-foreground';

export const TEMPLATE_CARD_END_CLASS =
    'ml-auto flex shrink-0 items-center gap-2';

export const TEMPLATE_CARD_BODY_CLASS = 'px-3.5 py-3';

export const TEMPLATE_CARD_BODY_COLUMN_CLASS = `${TEMPLATE_CARD_BODY_CLASS} flex flex-col gap-2.5`;

export const TEMPLATE_CARD_ROW_CLASS =
    'flex flex-wrap items-center gap-2.5 border-t border-[#E9F0F0] dark:border-border ' +
    'px-3.5 py-2.5 text-[13px]';

export const TEMPLATE_CARD_COMPACT_ROW_CLASS =
    'flex flex-wrap items-center gap-2.5 border-t border-[#E9F0F0] dark:border-border ' +
    'px-3.5 py-2 text-[13px]';

export const TEMPLATE_CARD_FOOTER_CLASS =
    'flex flex-wrap items-center gap-2 border-t border-[#E9F0F0] dark:border-border ' +
    'bg-[#F7FAFA] dark:bg-muted px-3.5 py-2.5';

export const TEMPLATE_CARD_FOOTER_ACTIONS_CLASS =
    'ml-auto flex flex-wrap items-center justify-end gap-2';

export const TEMPLATE_DAY_HEADER_CLASS =
    'flex items-center gap-2 px-3.5 pt-2 pb-1 text-[11px] font-semibold ' +
    'tracking-[0.05em] text-[#617578] dark:text-muted-foreground uppercase';

export const TEMPLATE_RANK_CHIP_CLASS =
    'flex size-[22px] shrink-0 items-center justify-center rounded-full ' +
    'bg-[#F0F5F5] dark:bg-muted text-[11px] font-semibold text-[#42595E] dark:text-muted-foreground';

export const TEMPLATE_TAG_CLASS =
    'inline-flex items-center rounded-md bg-[#E3F7FC] dark:bg-sky-950/50 px-2 py-0.5 ' +
    'text-[11px] font-medium whitespace-nowrap text-[#0054B5] dark:text-sky-300 ' +
    'shadow-[inset_0_0_0_1px_rgba(0,84,181,0.1)]';

export const TEMPLATE_TAG_POSITIVE_CLASS =
    'inline-flex items-center rounded-md bg-[#E9FCF0] dark:bg-emerald-950/50 px-2 py-0.5 ' +
    'text-[11px] font-medium whitespace-nowrap text-[#05614F] dark:text-emerald-300 ' +
    'shadow-[inset_0_0_0_1px_rgba(5,97,79,0.12)]';

export const TEMPLATE_TAG_WARNING_CLASS =
    'inline-flex items-center rounded-md bg-[#FFF2DB] dark:bg-amber-950/50 px-2 py-0.5 ' +
    'text-[11px] font-medium whitespace-nowrap text-[#8C3B00] dark:text-amber-300 ' +
    'shadow-[inset_0_0_0_1px_rgba(140,59,0,0.12)]';

export const TEMPLATE_TOKEN_CLASS =
    'inline-flex items-center rounded-full border border-[#DBE3E3] dark:border-border ' +
    'bg-[#F7FAFA] dark:bg-muted px-1.5 text-[10px] font-medium whitespace-nowrap ' +
    'text-[#42595E] dark:text-muted-foreground';

export const TEMPLATE_REPLY_CLASS =
    'inline-block w-fit max-w-[85%] rounded-xl rounded-bl px-3 py-1.5 ' +
    'text-[13px] leading-[1.4] text-[#002429] dark:text-foreground [overflow-wrap:anywhere] ' +
    'bg-[#F0F5F5] dark:bg-muted';

export const TEMPLATE_STATUS_CLASS: Record<StatusIntent, string> = {
    positive:
        'border-transparent bg-[#E9FCF0] dark:bg-emerald-950/50 text-[#05614F] dark:text-emerald-300',
    info: 'border-transparent bg-[#E3F7FC] dark:bg-sky-950/50 text-[#0054B5] dark:text-sky-300',
    warning:
        'border-transparent bg-[#FFF2DB] dark:bg-amber-950/50 text-[#8C3B00] dark:text-amber-300',
    negative:
        'border-transparent bg-[#FFF0F2] dark:bg-rose-950/50 text-[#A8002E] dark:text-rose-300',
    neutral:
        'border-transparent bg-[#F0F5F5] dark:bg-muted text-[#42595E] dark:text-muted-foreground',
};

// .approval-header: Yellow 50 band, Yellow 100 hairline, Yellow 600 text.
export const TEMPLATE_APPROVAL_BANNER_CLASS =
    'flex items-center gap-[7px] border-b border-[#FFDE9E] dark:border-amber-800/60 bg-[#FFF2DB] dark:bg-amber-950/50 ' +
    'px-4 py-2.5 text-[11px] font-semibold tracking-[0.04em] uppercase ' +
    'text-[#8C3B00] dark:text-amber-300';

export const TEMPLATE_APPROVAL_DOT_CLASS =
    'size-1.5 shrink-0 rounded-full bg-[#E89900]';

// .btn: pill, 36px tall, brand violet with the darker hover.
export const TEMPLATE_PRIMARY_BUTTON_CLASS =
    'inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 ' +
    'rounded-full border border-transparent bg-[#5533EE] px-4 text-sm ' +
    'font-medium whitespace-nowrap text-white ' +
    'shadow-[0_1px_2px_rgba(0,36,41,0.08)] ' +
    'transition-[background-color,transform,opacity] duration-150 ' +
    'hover:bg-[#4629C7] active:scale-[0.96] ' +
    'disabled:pointer-events-none disabled:opacity-50';

// .btn-outline: white pill, Grey 100 border, Grey 700 ink.
export const TEMPLATE_OUTLINE_BUTTON_CLASS =
    'inline-flex h-9 cursor-pointer items-center justify-center gap-1.5 ' +
    'rounded-full border border-[#DBE3E3] dark:border-border bg-white dark:bg-card px-4 text-sm ' +
    'font-medium whitespace-nowrap text-[#244045] dark:text-foreground ' +
    'shadow-[0_1px_0_rgba(0,36,41,0.04)] ' +
    'transition-[background-color,border-color,color,transform,opacity] ' +
    'duration-150 ' +
    'hover:border-[#BDC9C9] dark:hover:border-input hover:bg-[#F0F5F5] dark:hover:bg-muted hover:text-[#002429] dark:hover:text-foreground ' +
    'active:scale-[0.96] ' +
    'disabled:pointer-events-none disabled:opacity-50';

// .btn-sm variants for row-level actions.
export const TEMPLATE_PRIMARY_BUTTON_SM_CLASS =
    TEMPLATE_PRIMARY_BUTTON_CLASS.replace('h-9 ', 'h-8 ').replace(
        'px-4 text-sm',
        'px-3.5 text-[13px]',
    );

export const TEMPLATE_OUTLINE_BUTTON_SM_CLASS =
    TEMPLATE_OUTLINE_BUTTON_CLASS.replace('h-9 ', 'h-8 ').replace(
        'px-4 text-sm',
        'px-3.5 text-[13px]',
    );

export const TEMPLATE_PRIMARY_BUTTON_XS_CLASS =
    TEMPLATE_PRIMARY_BUTTON_CLASS.replace('h-9 ', 'h-[26px] ').replace(
        'px-4 text-sm',
        'px-[11px] text-xs',
    );

export const TEMPLATE_OUTLINE_BUTTON_XS_CLASS =
    TEMPLATE_OUTLINE_BUTTON_CLASS.replace('h-9 ', 'h-[26px] ').replace(
        'px-4 text-sm',
        'px-[11px] text-xs',
    );

// .approval-step-num states.
export const TEMPLATE_STEP_NUMBER_CLASS =
    'flex size-[22px] items-center justify-center rounded-full border ' +
    'text-[11px] font-semibold';

export const TEMPLATE_STEP_NUMBER_IDLE_CLASS =
    'border-[#DBE3E3] dark:border-border bg-[#F7FAFA] dark:bg-muted text-[#617578] dark:text-muted-foreground';

export const TEMPLATE_STEP_NUMBER_DONE_CLASS =
    'border-[#ADF2C7] dark:border-emerald-800/60 bg-[#E9FCF0] dark:bg-emerald-950/50 text-[#05614F] dark:text-emerald-300';

export const TEMPLATE_STEP_NUMBER_ACTIVE_CLASS =
    'border-[#5533EE] bg-[#5533EE] text-white';

// Ink scale (Cool Black and its greys).
export const TEMPLATE_INK_CLASS = 'text-[#002429] dark:text-foreground';
export const TEMPLATE_INK_SOFT_CLASS =
    'text-[#42595E] dark:text-muted-foreground';
export const TEMPLATE_INK_MUTED_CLASS =
    'text-[#617578] dark:text-muted-foreground';

// Violet chips for message variables (.pill-violet-soft treatment).
export const TEMPLATE_VARIABLE_CHIP_CLASS =
    'rounded-md bg-[#EEEAFD] dark:bg-violet-950/50 px-2 py-0.5 font-medium text-[#4629C7] dark:text-violet-300';

export const TEMPLATE_TABS_CLASS =
    'inline-flex h-[34px] items-center rounded-full bg-[#F0F5F5] dark:bg-muted p-[3px] ' +
    'text-[#617578] dark:text-muted-foreground';

export const TEMPLATE_TAB_CLASS =
    'h-7 cursor-pointer rounded-full border-0 bg-transparent px-[11px] ' +
    'text-[13px] font-medium text-inherit ' +
    'transition-[background-color,color,box-shadow,transform] duration-150 ' +
    'active:scale-[0.96] aria-pressed:bg-white dark:aria-pressed:bg-card ' +
    'aria-pressed:text-[#002429] dark:aria-pressed:text-foreground ' +
    'aria-pressed:shadow-[0_1px_2px_rgba(0,36,41,0.08)]';

export const TEMPLATE_FILTER_CHIP_CLASS =
    'inline-flex cursor-pointer items-center gap-1 rounded-full border ' +
    'border-[#DBE3E3] dark:border-border bg-white dark:bg-card px-2.5 py-[3px] text-xs text-[#42595E] dark:text-muted-foreground ' +
    'transition-[background-color,border-color,transform] duration-150 ' +
    'hover:border-[#BDC9C9] dark:hover:border-input hover:bg-[#F0F5F5] dark:hover:bg-muted active:scale-[0.96]';
